<template>
  <v-dialog v-model="edits.setlistModal" width="800" :persistent="true">
    <v-card>
      <v-card-title class="black">
        <h2 class="pl-2 text-uppercase font-weight-light">
          {{title}}
        </h2>
      </v-card-title>

      <v-card-text>
        <v-layout wrap>
          <v-flex xs12 sm4>
            <v-text-field
              label="Setlist Name *"
              color="secondary"
              v-model="formData.name"
              :rules="rules.name"
              :disabled="busy"/>
          </v-flex>
          <v-flex xs12 sm8>
            <v-text-field
              label="Description"
              color="secondary"
              v-model="formData.description"
              :disabled="busy"
              hint='Optional. Describe what this setlist is about.'/>
          </v-flex>
          <v-flex xs12 v-if="!isDynamic">
            <v-switch
              v-model="formData.public"
              :true-value="true"
              :false-value="false"
              color="secondary">
              <template v-slot:label>
                <p class="mb-0">Public<br/>
                <span class="caption">If unchecked, setlist will only be visible to the creator.</span></p>
              </template>
            </v-switch>
          </v-flex>
          <v-flex xs12 v-if="!isEdit">
            <v-switch
              v-model="formData.is_dynamic"
              :true-value="true"
              :false-value="false"
              color="secondary">
              <template v-slot:label>
                <p class="mb-0">
                  Dynamic Setlist<br/>
                  <span class="caption">If unchecked, songs must be added manually.<br/>
                  If checked, songs will be determined by users subscribed to the setlist.<br/>
                  Dynamic Setlists are always public.</span>
                </p>
              </template>
            </v-switch>
          </v-flex>
          <v-flex xs12 v-if="isDynamic">
            <v-layout wrap>
              <v-flex xs12 sm4>
                <v-text-field
                  label="Dynamic Setlist Size"
                  type="number"
                  color="secondary"
                  v-model="formData.dynamic_size"
                  :disabled="busy"
                  :persistent-hint="true"
                  hint='How many songs this setlist should generate.'/>
              </v-flex>
              <v-flex xs12 sm4>
                <v-select
                  :items="genres"
                  label="Dynamic Setlist Genre"
                  v-model="formData.dynamic_genre"
                  item-text="name"
                  item-value="id"
                  color="secondary"
                  :persistent-hint="true"
                  hint='Only add songs belonging to selected genre.'
                  :disabled="busy"/>
              </v-flex>
              <v-flex xs12 sm4>
                <v-menu
                  v-model="datePickerOpen"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px">
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="formData.dynamic_end_date"
                      label="Dynamic Setlist End Date"
                      readonly
                      v-on="on"
                      color="secondary"
                      :persistent-hint="true"
                      hint='Stop auto-refreshing after this date.'
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="formData.dynamic_end_date" @input="datePickerOpen = false"></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 class="mt-3 mb-3">
                <setlist-selector
                  :model-prop="formData.source_ids"
                  :excluded-ids="excludedSetlistIdsForSource"
                  @update="updateSourceIds"
                  color="green darken-3"
                  label="Setlist Sources"
                  hint="If specified, the dynamic setlist will only use songs from these setlists."
                  :persistent-hint="true"/>
              </v-flex>
              <v-flex xs12 class="mb-3">
                <setlist-selector
                  :model-prop="formData.exclusion_ids"
                  :excluded-ids="excludedSetlistIdsForExclusion"
                  @update="updateExclusionIds"
                  color="red darken-2"
                  label="Setlist Exclusions"
                  hint="If specified, the dynamic setlist will exclude any songs present in these setlists."
                  :persistent-hint="true"/>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12 class="mt-4">
                <b class="title">Rating and Training Scores</b><br/>
                Adjust the scores below to determine which songs are added to the setlist.
                Each score will be multiplied by subscribed user's ratings and training statuses.
                Songs that have a total score of <b>less than zero</b> will not be added to the setlist.

              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.rating_0_score">
                  <template v-slot:prepend><img :src="ratingEmojiFor(0)"/></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.rating_1_score">
                  <template v-slot:prepend><img :src="ratingEmojiFor(1)"/></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.rating_2_score">
                  <template v-slot:prepend><img :src="ratingEmojiFor(2)"/></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.rating_3_score">
                  <template v-slot:prepend><img :src="ratingEmojiFor(3)"/></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.rating_4_score">
                  <template v-slot:prepend><img :src="ratingEmojiFor(4)"/></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.rating_5_score">
                  <template v-slot:prepend><img :src="ratingEmojiFor(5)"/></template>
                </v-text-field>
              </v-flex>

              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.training_0_score">
                  <template v-slot:prepend><v-icon large :color="trainingColorFor('0')">{{trainingIconFor('0')}}</v-icon></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.training_N_score">
                  <template v-slot:prepend><v-icon large :color="trainingColorFor('N')">{{trainingIconFor('N')}}</v-icon></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.training_W_score">
                  <template v-slot:prepend><v-icon large :color="trainingColorFor('W')">{{trainingIconFor('W')}}</v-icon></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.training_P_score">
                  <template v-slot:prepend><v-icon large :color="trainingColorFor('P')">{{trainingIconFor('P')}}</v-icon></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.training_C_score">
                  <template v-slot:prepend><v-icon large :color="trainingColorFor('C')">{{trainingIconFor('C')}}</v-icon></template>
                </v-text-field>
              </v-flex>
              <v-flex xs3 sm2 class="pr-4">
                <v-text-field color="secondary" type="number" v-model="formData.training_F_score">
                  <template v-slot:prepend><v-icon large :color="trainingColorFor('F')">{{trainingIconFor('F')}}</v-icon></template>
                </v-text-field>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12><small class="ml-2">* Required fields</small></v-flex>
        </v-layout>

      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <div>
          <v-progress-circular indeterminate v-if="busy"></v-progress-circular>
          <v-btn color="secondary" flat @click="close()" :disabled="busy">Close</v-btn>
          <v-btn color="secondary black--text" @click="submit()" :disabled="busy">Save Setlist</v-btn>
        </div>
      </v-card-actions>
    </v-card>

  </v-dialog>
</template>

<script>

import RATINGS from '../constants/ratings'
import TRAINING_STATUSES from '../constants/training-statuses'
import GENRES from '../constants/genres'

import SetlistSelector from './SetlistSelector.vue'

const DYNAMIC_SETLIST_GENRES = [{ id: null, name: 'All Genres' }].concat(GENRES)

export default {
  name: 'setlist-edit-modal',
  props: {
    edits: Object
  },
  data () {
    return {
      busy: false,
      genres: DYNAMIC_SETLIST_GENRES,
      datePickerOpen: false,
      formData: {
        name: null,
        description: null,
        public: true,
        is_dynamic: false,
        dynamic_size: 30,
        dynamic_genre: null,
        dynamic_end_date: null,
        source_ids: [],
        exclusion_ids: [],
        rating_0_score: 0,
        rating_1_score: 0,
        rating_2_score: 0,
        rating_3_score: 0,
        rating_4_score: 0,
        rating_5_score: 0,
        training_0_score: 0,
        training_N_score: 0,
        training_W_score: 0,
        training_P_score: 0,
        training_C_score: 0,
        training_F_score: 0
      },
      rules: {
        name: [
          v => !!v || 'Name is required.'
        ]
      }
    }
  },
  computed: {
    title () {
      if (this.edits.setlist) {
        return `Edit Setlist: ${this.edits.setlist.id}`
      }
      return `Create Setlist`
    },
    isEdit () {
      return this.edits.setlist && this.edits.setlist.id
    },
    isDynamic () {
      return this.formData.is_dynamic
    },
    excludedSetlistIdsForSource () {
      var setlist = this.edits.setlist
      if (setlist && setlist.id) {
        return this.formData.exclusion_ids.concat([setlist.id])
      }
      return this.formData.exclusion_ids
    },
    excludedSetlistIdsForExclusion () {
      var setlist = this.edits.setlist
      if (setlist && setlist.id) {
        return this.formData.source_ids.concat([setlist.id])
      }
      return this.formData.source_ids
    }
  },
  methods: {
    refreshData () {
      var setlist = this.edits.setlist
      if (setlist) {
        this.formData.name = setlist.name
        this.formData.description = setlist.description
        this.formData.public = setlist.public
        this.formData.is_dynamic = setlist.is_dynamic
        this.formData.dynamic_size = setlist.dynamic_size
        this.formData.dynamic_genre = setlist.dynamic_genre
        this.formData.dynamic_end_date = (setlist.dynamic_end_date || '').split('T')[0]
        this.formData.source_ids = setlist.source_ids || []
        this.formData.exclusion_ids = setlist.exclusion_ids || []
        this.formData.rating_0_score = setlist.rating_0_score
        this.formData.rating_1_score = setlist.rating_1_score
        this.formData.rating_2_score = setlist.rating_2_score
        this.formData.rating_3_score = setlist.rating_3_score
        this.formData.rating_4_score = setlist.rating_4_score
        this.formData.rating_5_score = setlist.rating_5_score
        this.formData.training_0_score = setlist.training_0_score
        this.formData.training_N_score = setlist.training_N_score
        this.formData.training_W_score = setlist.training_W_score
        this.formData.training_P_score = setlist.training_P_score
        this.formData.training_C_score = setlist.training_C_score
        this.formData.training_F_score = setlist.training_F_score
      } else {
        this.resetData()
      }
    },
    resetData () {
      this.formData.name = null
      this.formData.description = null
      this.formData.public = true
      this.formData.is_dynamic = false
      this.formData.dynamic_size = 30
      this.formData.dynamic_genre = null
      this.formData.dynamic_end_date = null
      this.formData.source_ids = []
      this.formData.exclusion_ids = []
      this.formData.rating_0_score = 0
      this.formData.rating_1_score = 0
      this.formData.rating_2_score = 0
      this.formData.rating_3_score = 0
      this.formData.rating_4_score = 0
      this.formData.rating_5_score = 0
      this.formData.training_0_score = 0
      this.formData.training_N_score = 0
      this.formData.training_W_score = 0
      this.formData.training_P_score = 0
      this.formData.training_C_score = 0
      this.formData.training_F_score = 0
    },
    submit () {
      this.busy = true
      var promise
      var setlist = this.edits.setlist
      if (setlist && setlist.id) {
        promise = this.$api.setlists.update(setlist.id, this.formData)
      } else {
        promise = this.$api.setlists.create(this.formData)
      }
      return promise.then(savedSetlist => {
        this.$router.push({
          path: `/setlists/${savedSetlist.id}`,
          query: { updated: +(new Date()) }
        }).catch(_err => {}) // avoid NavigationDuplicated errors as the modal can be called from SetlistInfo
        this.close()
        this.busy = false
      }).catch(error => {
        console.log(error)
        this.busy = false
      })
    },
    close () {
      this.edits.setlistModal = false
    },
    ratingEmojiFor (rating) {
      return RATINGS['' + rating].emoji
    },
    trainingIconFor (status) {
      return TRAINING_STATUSES.get('' + status).i
    },
    trainingColorFor (status) {
      return TRAINING_STATUSES.get('' + status).c
    },
    updateSourceIds (ids) {
      this.formData.source_ids = ids
    },
    updateExclusionIds (ids) {
      this.formData.exclusion_ids = ids
    }
  },
  watch: {
    'edits.setlistModal': function (val) {
      if (val === true) {
        this.refreshData()
      } else {
        this.resetData()
        this.edits.setlist = null
      }
    },
    'formData.is_dynamic': function (_val) {
      this.formData.public = true
    },
    'formData.public': function (val) {
      if (val === false) {
        this.formData.is_dynamic = false
      }
    }
  },
  components: {
    SetlistSelector
  }
}
</script>
